// Entry point Vite. Bridge moduli ESM → window.* per <script> classic inline.
// Cleanup di tutti i window.* in PR-10.

// PR-9: CSS estratto in coach/src/style.css. Vite lo bundla automaticamente
// e in build estrae a dist/assets/coach-XXX.css con <link> auto-injected
// nei HTML che importano main.js (rollup HTML entry chain).
import './style.css';

import { api, cj, fixJson, parseLightMarkdown, SONNET, HAIKU, logEvent, getLastUsage } from './core.js';
import { swapSingleRecipe } from './meal-recipe-composer.js';
import { getSys, workP1, workP2, strP, normDur } from './prompts.js';
import * as builders from './builders.js';
import * as generators from './generators.js';
import * as auth from './ui-auth.js';
import * as chat from './ui-chat.js';
import * as home from './ui-home.js';
import * as plans from './ui-plans.js';
import * as profile from './ui-profile.js';
import * as health from './ui-health.js';
import * as onboarding from './ui-onboarding.js';
import * as survey from './ui-survey.js';
import { ANNOUNCEMENT_VERSION, ANNOUNCEMENT_BODY_MD } from './announcement-config.js';
// BKL-016 PR 2: init state globale (side-effect import, no exports).
// Inizializza window.S, window.PS, window._welcomeFlags usati cross-modulo
// da builders, generators, ui-*, e dallo <script> classic inline residuo.
import './state-init.js';
// BKL-016 PR 3 / BKL-086: AUTH init (Supabase client + onAuthStateChange +
// visibilitychange). Side-effect import — crea window.sb, registra listener.
// IMPORTANTE: deve eseguire DOPO state-init.js (dipendenze window._welcomeFlags)
// MA i bridge window.loadUserData / window.updateChatHeroVisibility / window._forceLogoutToLogin
// sono settati PIÙ AVANTI in questo file (Object.assign chain) → safe perché
// onAuthStateChange listener fires async, post-modulo-eseguito.
import './auth-init.js';
// BKL-016 PR 4: DOM listeners residui (Esc key handler, loginEmail Enter,
// pointer-tracker IIFE, topbar height sync IIFE). Side-effect import.
// Dipendenze: window.closeWelcome, window.sendMagicLink, window.initHomeChartSwitcher
// (typeof guard per safety).
import './listeners-bridge.js';

console.log('[main] Vite bootstrap', import.meta.env.VITE_APP_ENV);

// BKL-016 PR 1 (ADR-0005 completare split): ANNOUNCEMENT_* estratte da
// <script> classic inline. Esposte su window per consumo da ui-onboarding.js
// `shouldShowAnnouncement` (che legge `window.ANNOUNCEMENT_VERSION` perché
// rappresenta valore "configurazione runtime", facilmente swappable).
window.ANNOUNCEMENT_VERSION = ANNOUNCEMENT_VERSION;
window.ANNOUNCEMENT_BODY_MD = ANNOUNCEMENT_BODY_MD;

// PR-2: prompts (template Sonnet/Haiku)
window.getSys = getSys;
window.workP1 = workP1;
window.workP2 = workP2;
window.strP = strP;
window.normDur = normDur;

// PR-3a: core helpers (api wrapper Anthropic + JSON cleanup) + costanti modelli
window.api = api;
window.getLastUsage = getLastUsage;
window.cj = cj;
window.fixJson = fixJson;
window.parseLightMarkdown = parseLightMarkdown;
window.SONNET = SONNET;
window.HAIKU = HAIKU;
// BKL-014: analytics helper — esposto su window per uso da moduli e da iframe
window.logEvent = logEvent;

// PR-3b: builders (DB persistence + helpers ingredient match/parse)
//   NB: builders consuma window.sb / window.currentUser / window.S /
//   window._welcomeFlags esposti dallo <script> classic inline.
Object.assign(window, builders);

// PR-3c: generators (genAll / genMeal / genWork / genStr / genSpesa) +
//   gates (profileGaps, dietClarificationNeeded, varietyClarificationNeeded) +
//   meal helpers (MEAL_RULES, deduceStili, mealRecap, buildProteinSchedule,
//   mealDayTags). Consumano window.S/PS + UI inline (setTile, addMsg, ecc.).
Object.assign(window, generators);

// PR-4: ui-auth (login/signup/reset/logout). _authMode è state interno al
//   modulo. Listener sb.auth.onAuthStateChange e visibilitychange restano
//   nello <script> classic inline (registrazione sync al parse time).
Object.assign(window, auth);

// PR-5: ui-chat (send + busy + open/close panel + msg helpers + onboarding
//   conv persistence). busy è state interno al modulo.
Object.assign(window, chat);

// PR-6: ui-home (renderHomeDayCard, renderHomeMiniChart, setTile, updateDL,
//   updateBadge). homeChartInst è state interno. setTile importa getBusy
//   da ui-chat per disabilitare bottoni 'Riprova' durante busy.
Object.assign(window, home);

// PR-7: ui-plans (HTML builders meal/workout/stretch/spesa + libreria
//   SVG/icone + CSS/timer + openPlan + retryPlan + helpers normDiet/
//   stretchDayTags). retryPlan ora usa getBusy()/setBusy() invece del
//   bare identifier `busy`. La chiamata legacy `save()` (funzione
//   inesistente) è stata rimossa.
Object.assign(window, plans);

// PR-8: ui-profile (form profilo + sheet read-only + menu drawer + body
//   measurements CRUD/chart). measFilter/measChartInst sono state interno
//   al modulo. Le funzioni interagiscono con window.S/sb/currentUser e
//   tornano a chiamare addMsg/setBusy/saveProfile/genAll/updateBadge
//   tramite import ESM (no più window.* per funzioni cross-modulo).
Object.assign(window, profile);
Object.assign(window, health);

// PR-10: ui-onboarding (welcome wizard + email consent + legal acceptance +
//   loadUserData orchestratore init). _welcomeCurrentSlide / WELCOME_SLIDE_COUNT
//   / _consentOpenChatAfter sono state interno al modulo. window._welcomeFlags
//   resta esposto (consumato anche dall'<script> classic inline e da builders).
//   loadUserData chiamata dal listener inline `sb.auth.onAuthStateChange` come
//   bare identifier `loadUserData()` → resolve via window dopo Object.assign.
Object.assign(window, onboarding);

// Dashboard sondaggio (solo admin): openSurveyDashboard/closeSurveyDashboard/
// setSurveySegment/genSurveyInsights + initSurveyMenu (auto-inietta la voce di
// menu se profile.is_admin). Legge il CSV pubblicato del Google Form, aggrega
// e disegna con Chart.js; insight via window.api (proxy /api/anthropic).
Object.assign(window, survey);

// BKL-030: swap singola ricetta da drawer piano alimentare.
// Chiamato da iframe via parent._swapMeal(di, mi, ii, slot, recipeId, btn).
// btn = riferimento DOM al pulsante (same-origin iframe → accessibile da parent),
// usato per resettare lo stato `loading` se non c'è alternativa o se l'op fallisce.
const _SLOT_KCAL_PCT = { col: 0.15, sna1: 0.10, sna2: 0.10, pra: 0.30, cen: 0.30 };
const _resetSwapBtn = (btn) => {
  if (btn && btn.classList) {
    btn.classList.remove('loading');
    btn.disabled = false;
  }
};
window._swapMeal = async function(di, mi, ii, slot, recipeId, btn) {
  const profile = window.S?.profile;
  const planData = window.S?.plans?.meal;
  if (!profile || !planData?.days) { _resetSwapBtn(btn); return; }
  const kcal = profile.kcal_target || 2000;
  const slotKcal = Math.round(kcal * (_SLOT_KCAL_PCT[slot] || 0.25));
  try {
    const newItem = await swapSingleRecipe(window.sb, slot, profile, recipeId, slotKcal);
    if (!newItem) {
      _resetSwapBtn(btn);
      alert('Nessuna alternativa disponibile per questo slot. Prova ad ampliare le cucine attive o le preferenze.');
      return;
    }
    const meal = planData.days[di]?.m?.[mi];
    if (!meal?.items?.[ii]) { _resetSwapBtn(btn); return; }
    meal.items[ii] = newItem;
    const planId = planData._id;
    if (planId) {
      // BKL-007 fix: Supabase v2+ query builder non ha .catch() sulla chain.
      // Destructure {error} dalla Promise — silently log se serve.
      const { error: planUpdErr } = await window.sb.from('plans').update({ data: planData }).eq('id', planId);
      if (planUpdErr) console.warn('plan update post-swap failed:', planUpdErr);
    }
    // L'iframe viene ricaricato → il bottone viene ricreato pulito (loading auto-reset).
    plans.openPlan('meal');
  } catch (err) {
    _resetSwapBtn(btn);
    console.error('swapSingleRecipe failed:', err);
    alert('Errore durante la sostituzione della ricetta. Riprova.');
  }
};

// BKL-036 fix 2026-05-15 (sessione): handler completion eseguiti nel parent
// invece che dentro l'iframe srcdoc. Motivo: pattern già esistente (vedi
// _swapMeal sopra). L'iframe chiama parent.<handler>(...) — accesso diretto
// a window.sb / window.currentUser / window.S senza dipendere da `parent.X`
// cross-frame che era inaffidabile (0 record DB pre-fix).

// Meal completions
window._persistMealCompletion = function(di, slot, ii, on) {
  const uid = window.currentUser && window.currentUser.id;
  const pid = window.S && window.S.plans && window.S.plans.meal && window.S.plans.meal._id;
  if (!uid || !pid) { console.warn('[meal completion] missing uid/pid', { uid, pid }); return; }
  if (on) {
    window.sb.from('meal_completions').upsert({
      user_id: uid, plan_id: pid, day_index: di, slot, item_index: ii,
      completed_at: new Date().toISOString()
    }, { onConflict: 'user_id,plan_id,day_index,slot,item_index' })
      .then(({ error }) => {
        if (error) console.error('[meal completion upsert]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('meal');
      });
  } else {
    window.sb.from('meal_completions').delete()
      .eq('user_id', uid).eq('plan_id', pid).eq('day_index', di).eq('slot', slot).eq('item_index', ii)
      .then(({ error }) => {
        if (error) console.error('[meal completion delete]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('meal');
      });
  }
};
window._loadMealCompletions = function() {
  const uid = window.currentUser && window.currentUser.id;
  const pid = window.S && window.S.plans && window.S.plans.meal && window.S.plans.meal._id;
  if (!uid || !pid) { console.warn('[meal load] missing uid/pid', { uid, pid }); return Promise.resolve([]); }
  return window.sb.from('meal_completions').select('day_index,slot,item_index,completed_at')
    .eq('user_id', uid).eq('plan_id', pid)
    .then(({ data, error }) => {
      if (error) { console.error('[meal load]', error); return []; }
      return data || [];
    });
};

// Workout completions
window._persistWorkoutCompletion = function(exerciseKey, on) {
  const uid = window.currentUser && window.currentUser.id;
  const pid = window.S && window.S.plans && window.S.plans.workout && window.S.plans.workout._id;
  if (!uid || !pid) { console.warn('[workout completion] missing uid/pid', { uid, pid }); return; }
  if (on) {
    window.sb.from('workout_completions').upsert({ user_id: uid, plan_id: pid, exercise_key: exerciseKey })
      .then(({ error }) => {
        if (error) console.error('[workout completion upsert]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('workout');
      });
  } else {
    window.sb.from('workout_completions').delete()
      .eq('user_id', uid).eq('plan_id', pid).eq('exercise_key', exerciseKey)
      .then(({ error }) => {
        if (error) console.error('[workout completion delete]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('workout');
      });
  }
};
window._loadWorkoutCompletions = function() {
  const uid = window.currentUser && window.currentUser.id;
  const pid = window.S && window.S.plans && window.S.plans.workout && window.S.plans.workout._id;
  if (!uid || !pid) { console.warn('[workout load] missing uid/pid', { uid, pid }); return Promise.resolve([]); }
  // SELECT exercise_key + completed_at: filtro settimana ISO richiede timestamp
  return window.sb.from('workout_completions').select('exercise_key, completed_at')
    .eq('user_id', uid).eq('plan_id', pid)
    .then(({ data, error }) => {
      if (error) { console.error('[workout load]', error); return []; }
      return data || [];
    });
};

// Stretch completions (stesso pattern)
window._persistStretchCompletion = function(exerciseKey, on) {
  const uid = window.currentUser && window.currentUser.id;
  const pid = window.S && window.S.plans && window.S.plans.stretch && window.S.plans.stretch._id;
  if (!uid || !pid) { console.warn('[stretch completion] missing uid/pid', { uid, pid }); return; }
  if (on) {
    window.sb.from('stretch_completions').upsert({ user_id: uid, plan_id: pid, exercise_key: exerciseKey })
      .then(({ error }) => {
        if (error) console.error('[stretch completion upsert]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('stretch');
      });
  } else {
    window.sb.from('stretch_completions').delete()
      .eq('user_id', uid).eq('plan_id', pid).eq('exercise_key', exerciseKey)
      .then(({ error }) => {
        if (error) console.error('[stretch completion delete]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('stretch');
      });
  }
};
window._loadStretchCompletions = function() {
  const uid = window.currentUser && window.currentUser.id;
  const pid = window.S && window.S.plans && window.S.plans.stretch && window.S.plans.stretch._id;
  if (!uid || !pid) { console.warn('[stretch load] missing uid/pid', { uid, pid }); return Promise.resolve([]); }
  // SELECT exercise_key + completed_at: streak Duolingo richiede timestamp
  return window.sb.from('stretch_completions').select('exercise_key, completed_at')
    .eq('user_id', uid).eq('plan_id', pid)
    .then(({ data, error }) => {
      if (error) { console.error('[stretch load]', error); return []; }
      return data || [];
    });
};

// Shopping completions (spunte lista spesa) — mirror pattern di workout/stretch.
// Tabella shopping_completions: (user_id, plan_id, item_key, completed_at) + UNIQUE upsert.
window._persistShoppingCompletion = function(itemKey, on) {
  const uid = window.currentUser && window.currentUser.id;
  // plan_id: spesa ha proprio _id, fallback a meal._id per legacy
  const shoppingPlan = window.S && window.S.plans && (window.S.plans.spesa || window.S.plans.meal);
  const pid = shoppingPlan && shoppingPlan._id;
  if (!uid || !pid) { console.warn('[shopping completion] missing uid/pid', { uid, pid }); return; }
  if (on) {
    window.sb.from('shopping_completions').upsert({ user_id: uid, plan_id: pid, item_key: itemKey })
      .then(({ error }) => {
        if (error) console.error('[shopping completion upsert]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('shopping');
      });
  } else {
    window.sb.from('shopping_completions').delete()
      .eq('user_id', uid).eq('plan_id', pid).eq('item_key', itemKey)
      .then(({ error }) => {
        if (error) console.error('[shopping completion delete]', error);
        else if (typeof window._notifyCompletionChange === 'function') window._notifyCompletionChange('shopping');
      });
  }
};

window._loadShoppingCompletions = function() {
  const uid = window.currentUser && window.currentUser.id;
  const shoppingPlan = window.S && window.S.plans && (window.S.plans.spesa || window.S.plans.meal);
  const pid = shoppingPlan && shoppingPlan._id;
  if (!uid || !pid) return Promise.resolve([]);
  return window.sb.from('shopping_completions').select('item_key, completed_at')
    .eq('user_id', uid).eq('plan_id', pid)
    .then(({ data, error }) => {
      if (error) { console.error('[shopping load]', error); return []; }
      return data || [];
    });
};

// ─── Re-render trigger — invalidate cache + ri-renderizza home widgets ──────
// Chiamato dopo upsert/delete completion (meal/workout/stretch/shopping) per
// aggiornare automaticamente i count in home senza F5 manuale.
// Idempotency: invalidation è O(1), no loop perché _notifyCompletionChange
// non re-emette eventi di completion.
window._notifyCompletionChange = function(tipo) {
  // Invalida stats cache (forza re-fetch al prossimo render)
  if (window.S) {
    window.S._homeStats = null;
    window.S._homeStatsAt = 0;
  }
  // Re-render fire-and-forget
  if (typeof window.renderHomeWidgets === 'function') {
    Promise.resolve(window.renderHomeWidgets()).catch(e => console.warn('[notify] renderHomeWidgets failed:', e));
  }
  if (typeof window.renderPlanTilesMini === 'function') {
    Promise.resolve(window.renderPlanTilesMini()).catch(e => console.warn('[notify] renderPlanTilesMini failed:', e));
  }
};
